import { useCallback } from 'react'
import { useMutation, useQueryClient } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { confirmFecAccountMappings, confirmFecJournalMappings, confirmFecThirdPartyMappings } from '../query-api'
import type { FecImportItem } from '../query-api'
import { isFecConfirmationIncomplete } from '../utils/isFecConfirmationIncomplete'
import { shouldConfirmFecThirdParties } from '../utils/shouldConfirmFecThirdParties'

type ConfirmableFecImport = Pick<
  FecImportItem,
  | 'accountMappingStatus'
  | 'accountPushStatus'
  | 'entryPushStatus'
  | 'id'
  | 'journalMappingStatus'
  | 'journalPushStatus'
  | 'thirdPartyMappingStatus'
  | 'thirdPartyPushStatus'
>

const confirmFecMappings = async (organizationId: string, fecImports: ConfirmableFecImport[]): Promise<void> => {
  // A resumed import can already have a domain confirmed and pushed: there is nothing left to confirm there.
  const results = await Promise.allSettled(
    fecImports.flatMap(
      ({
        accountMappingStatus,
        accountPushStatus,
        entryPushStatus,
        id,
        journalMappingStatus,
        journalPushStatus,
        thirdPartyMappingStatus,
        thirdPartyPushStatus,
      }) => [
        ...(isFecConfirmationIncomplete(journalMappingStatus, journalPushStatus)
          ? [confirmFecJournalMappings(organizationId, id)]
          : []),
        ...(isFecConfirmationIncomplete(accountMappingStatus, accountPushStatus)
          ? [confirmFecAccountMappings(organizationId, id)]
          : []),
        ...(shouldConfirmFecThirdParties({ entryPushStatus, thirdPartyMappingStatus, thirdPartyPushStatus })
          ? [confirmFecThirdPartyMappings(organizationId, id)]
          : []),
      ],
    ),
  )

  const failureReasons = results.flatMap((result) => (result.status === 'rejected' ? [result.reason] : []))

  if (failureReasons.length > 0) {
    const messages = failureReasons.map((reason) => (reason instanceof Error ? reason.message : String(reason)))

    throw new Error(`FEC mappings confirmation failed: ${messages.join('; ')}`)
  }
}

interface UseConfirmFecMappingsResult {
  confirm: () => Promise<void>
  isError: boolean
  isProcessing: boolean
}

export const useConfirmFecMappings = (fecImports: ConfirmableFecImport[]): UseConfirmFecMappingsResult => {
  const { organization } = useUserOrganization()
  const organizationId = organization?.id || ''
  const queryClient = useQueryClient()

  const {
    isError,
    isLoading: isProcessing,
    mutateAsync,
  } = useMutation(() => confirmFecMappings(organizationId, fecImports), {
    // Also after a failure: one domain can be confirmed while the other failed, and the retry must see that.
    // Returned, so the mutation only settles once the list holds the new statuses.
    onSettled: () => queryClient.invalidateQueries([QueryKeys.FecImports, organizationId]),
  })

  const confirm = useCallback(() => mutateAsync(), [mutateAsync])

  return {
    confirm,
    isError,
    isProcessing,
  }
}
